import { getAllSites } from '../SiteManagement/siteApi';
import { normalizeSites } from '../QR/qrFacilityData';
import { isLikelyOpaqueId, lostAndFoundApi } from '../LostAndFound/lostAndFoundApi';

/** Short due reference when API has no human-readable due number. */
export const formatDueRef = (dueId) => {
  const id = String(dueId || '').trim();
  if (!id) return '—';
  return `Due #${id.slice(0, 8).toUpperCase()}`;
};

export const formatUnitLabel = ({ blockLabel, flatLabel, blockId, flatId } = {}) => {
  const block =
    blockLabel ||
    (blockId && !isLikelyOpaqueId(blockId) ? String(blockId) : '');
  const flat =
    flatLabel || (flatId && !isLikelyOpaqueId(flatId) ? String(flatId) : '');
  if (block && flat) return `${block} · ${flat}`;
  if (flat) return flat;
  if (block) return block;
  return '—';
};

/** flat_id -> { flatLabel, blockLabel } for a site (scans blocks/flats APIs). */
export async function buildFlatLabelIndex(siteId) {
  const byFlatId = new Map();
  if (!siteId) return byFlatId;

  try {
    const blocks = await lostAndFoundApi.getBlocks(siteId);
    for (const block of blocks) {
      let flats = [];
      try {
        flats = await lostAndFoundApi.getFlats(siteId, block.id);
      } catch {
        continue;
      }
      for (const flat of flats) {
        byFlatId.set(String(flat.id), {
          flatLabel: flat.label,
          blockLabel: block.label,
        });
      }
    }
  } catch {
    /* ignore — labels stay empty */
  }

  return byFlatId;
}

/** Find which block contains flat_id (for edit prefill when block_id is missing). */
export async function findBlockIdForFlat(siteId, flatId) {
  if (!siteId || !flatId) return '';
  try {
    const blocks = await lostAndFoundApi.getBlocks(siteId);
    for (const block of blocks) {
      try {
        const flats = await lostAndFoundApi.getFlats(siteId, block.id);
        if (flats.some((f) => String(f.id) === String(flatId))) {
          return String(block.id);
        }
      } catch {
        continue;
      }
    }
  } catch {
    /* ignore */
  }
  return '';
}

const pickFirst = (...values) => {
  for (const v of values) {
    const s = v != null ? String(v).trim() : '';
    if (s) return s;
  }
  return '';
};

/**
 * Resolve site / block / flat / due display strings for UI.
 * Uses API-enriched fields when present, otherwise facility + site lookups.
 */
export async function resolveDueDisplayLabels(due) {
  if (!due) {
    return {
      siteName: '—',
      blockLabel: '',
      flatLabel: '',
      unitLabel: '—',
      dueRef: '—',
      dueIdFull: '—',
    };
  }

  const siteId = pickFirst(due.site_id, due.siteId);
  const flatId = pickFirst(due.flat_id, due.flatId);
  const blockId = pickFirst(due.block_id, due.blockId);
  const dueId = pickFirst(due.due_id, due.dueId, due.id);

  let siteName = pickFirst(due.site_name, due.siteName, due.site);
  let blockLabel = pickFirst(due.block_name, due.blockName, due.block_label);
  let flatLabel = pickFirst(
    due.flat_name,
    due.flatName,
    due.flat_label,
    due.flat_no,
    due.flat_number,
    due.unit_no
  );

  if (due.unit && !isLikelyOpaqueId(due.unit)) {
    flatLabel = flatLabel || String(due.unit).trim();
  }

  if (siteId && !siteName) {
    try {
      const sites = normalizeSites(await getAllSites());
      const match = sites.find((s) => String(s.site_id) === String(siteId));
      siteName = pickFirst(match?.site_name, match?.name);
    } catch {
      /* ignore */
    }
  }

  if (siteId && flatId && !flatLabel) {
    try {
      if (blockId) {
        const blocks = await lostAndFoundApi.getBlocks(siteId);
        if (!blockLabel) {
          blockLabel =
            blocks.find((b) => String(b.id) === String(blockId))?.label || '';
        }
        const flats = await lostAndFoundApi.getFlats(siteId, blockId);
        flatLabel =
          flats.find((f) => String(f.id) === String(flatId))?.label || '';
      }
      if (!flatLabel) {
        const index = await buildFlatLabelIndex(siteId);
        const hit = index.get(String(flatId));
        if (hit) {
          flatLabel = hit.flatLabel || '';
          blockLabel = blockLabel || hit.blockLabel || '';
        }
      }
    } catch {
      /* ignore */
    }
  }

  const dueRef = pickFirst(due.reference_number, due.referenceNumber) || formatDueRef(dueId);

  return {
    siteName: siteName || '—',
    blockLabel,
    flatLabel,
    unitLabel: formatUnitLabel({ blockLabel, flatLabel, blockId, flatId }),
    dueRef,
    dueIdFull: dueId || '—',
  };
};

/** Merge flat/block labels from a prebuilt index into a due record. */
export const applyFlatLabelIndex = (due, index) => {
  if (!due || !index) return due;
  const flatId = due.flat_id || due.flatId;
  if (!flatId) return due;
  const hit = index.get(String(flatId));
  if (!hit) return due;
  return {
    ...due,
    flat_label: due.flat_label || hit.flatLabel,
    flat_name: due.flat_name || hit.flatLabel,
    block_label: due.block_label || hit.blockLabel,
    block_name: due.block_name || hit.blockLabel,
  };
};
